/**
 * lib/photoReminders.ts
 * ------------------------------------------------------------------
 * The photo reminder to parents, as the Reminders tab of Photo Management
 * edits it. MIRROR of functions/lib/photoReminderText.js (functions cannot
 * import from src/) — the defaults, the frequencies and the placeholders
 * must match. Change one, change both.
 * ------------------------------------------------------------------ */

export const PHOTO_REMINDER_SETTINGS = 'photoReminderSettings';
export const PHOTO_REMINDER_LOG = 'photoReminderLog';

export interface PhotoReminderSettings {
  enabled: boolean;
  frequencyDays: number;
  subject: string;
  body: string;
  /** The "what we need" message sent when a photo is removed. */
  standard?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export const DEFAULT_PHOTO_REMINDER: PhotoReminderSettings = {
  enabled: false,
  frequencyDays: 7,
  subject: 'Please add a photo of {children} to the school portal',
  body: [
    'Dear {parent},',
    '',
    'We do not yet have a photo of {children} on the {school} portal. The photo is used on reports and school records.',
    '',
    'Please sign in to the Parent Portal and press “Add photo” at the top of the page — the button in this email takes you straight there. It takes a minute from your phone. Use a recent, clear photo of your child’s face on a plain background.',
    '',
    '{link}',
    '',
    'Thank you,',
    '{school}',
  ].join('\n'),
};

/** What the parent is told when a photo is removed for not meeting the
 *  standard. {child}, {school} and {link} are filled in. */
export const DEFAULT_PHOTO_STANDARD = "The photo we received of {child} does not meet the school's standard, so it has been removed. Please upload a new one from {child}'s page on the Parent Portal:\n\n- a recent photo, taken in the last few months\n- {child} alone — head and shoulders, facing the camera\n- a plain, light background\n- good light and in focus, with no filters or stickers\n- no hats, caps or sunglasses\n\n{link}";

export const FREQUENCY_CHOICES: { days: number; label: string }[] = [
  { days: 1, label: 'Every day' },
  { days: 3, label: 'Every 3 days' },
  { days: 7, label: 'Every week' },
  { days: 14, label: 'Every 2 weeks' },
  { days: 30, label: 'Every month' },
];

export const PLACEHOLDERS: { token: string; means: string }[] = [
  { token: '{parent}', means: 'the parent’s name, or “Parent/Guardian”' },
  { token: '{children}', means: 'their children still without a photo, e.g. “Neo and Kabo”' },
  { token: '{school}', means: 'the school’s name' },
  { token: '{link}', means: 'the Parent Portal address' },
];

export function childList(names: string[]): string {
  const firsts = names.map((n) => String(n || '').trim()).filter(Boolean);
  if (firsts.length <= 1) return firsts[0] || 'your child';
  return `${firsts.slice(0, -1).join(', ')} and ${firsts[firsts.length - 1]}`;
}

export function fillTemplate(text: string, vars: Record<string, string>): string {
  return String(text || '').replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] !== undefined ? vars[k] : m));
}

export function effectiveSettings(saved?: Partial<PhotoReminderSettings> | null): PhotoReminderSettings {
  const s = { ...DEFAULT_PHOTO_REMINDER, ...(saved || {}) };
  if (!FREQUENCY_CHOICES.some((f) => f.days === Number(s.frequencyDays))) s.frequencyDays = DEFAULT_PHOTO_REMINDER.frequencyDays;
  if (!String(s.subject || '').trim()) s.subject = DEFAULT_PHOTO_REMINDER.subject;
  if (!String(s.body || '').trim()) s.body = DEFAULT_PHOTO_REMINDER.body;
  if (!String(s.standard || '').trim()) s.standard = DEFAULT_PHOTO_STANDARD;
  s.enabled = s.enabled === true;
  return s;
}
